import {
  BOOST_RULE_TYPES,
  DEFAULT_FORM,
  DEFAULT_PLACEMENT_BY_TYPE,
  FRIENDLY_FILTER_KEYS,
  RULE_KIND_CONFIG,
} from './ruleConstants'
import {
  formatDateTimeLocal,
  formatJson,
  numberOrEmpty,
  parseJsonField,
  parseTags,
  pickFirstList,
  pickFirstText,
} from './formatters'

export function ruleToForm(rule) {
  const filters = rule.filter_logic || {}
  const config = rule.config || {}
  const sortLogic = rule.sort_logic || {}
  const payout = filters.payout_level
  const payoutLevel = typeof payout === 'object' && payout !== null ? payout.gte || payout.min || '' : payout || ''
  const maxWin = filters.max_win_min || filters.max_win_multiplier_min || ''
  const trigger = config.trigger && typeof config.trigger === 'object' ? config.trigger : {}
  const boostStrength = sortLogic.boost ?? config.boost ?? DEFAULT_FORM.boost_strength

  return {
    ...DEFAULT_FORM,
    id: rule.id,
    rule_type: rule.rule_type || DEFAULT_FORM.rule_type,
    label: rule.label || '',
    title: rule.title || '',
    query: rule.query || '',
    placement: rule.placement || DEFAULT_PLACEMENT_BY_TYPE[rule.rule_type] || DEFAULT_FORM.placement,
    sort_order: rule.sort_order ?? DEFAULT_FORM.sort_order,
    market: rule.market || '',
    segment: rule.segment || '',
    is_active: Boolean(rule.is_active),
    starts_at: formatDateTimeLocal(rule.starts_at),
    ends_at: formatDateTimeLocal(rule.ends_at),
    provider: pickFirstText(filters.provider, filters.provider_name, filters.provider_inferred),
    game_codes: pickFirstList(filters.game_codes, filters.game_code, filters.ids),
    promo_type: pickFirstText(filters.promo_type),
    jackpot_type: pickFirstText(filters.jackpot_type),
    category: pickFirstText(filters.category, filters.categories),
    volatility: pickFirstText(filters.volatility, filters.volatility_band),
    payout_level: payoutLevel ? String(payoutLevel) : '',
    max_win_min: maxWin ? String(maxWin) : '',
    theme_tags: Array.isArray(filters.theme_tags) ? filters.theme_tags.join(', ') : pickFirstText(filters.theme_tags),
    weekday_days: pickFirstList(filters.weekday_days),
    time_start: pickFirstText(filters.time_start),
    time_end: pickFirstText(filters.time_end),
    tone: config.tone || DEFAULT_FORM.tone,
    boost_strength: numberOrEmpty(boostStrength) || DEFAULT_FORM.boost_strength,
    sort_strategy: sortLogic.strategy || config.sort_strategy || DEFAULT_FORM.sort_strategy,
    after_rows: numberOrEmpty(trigger.after_rows ?? config.after_rows) || DEFAULT_FORM.after_rows,
    filter_logic: formatJson(filters),
    sort_logic: formatJson(sortLogic),
    config: formatJson(config),
  }
}

function removeFriendlyFilterKeys(filters) {
  const next = { ...filters }
  FRIENDLY_FILTER_KEYS.forEach((key) => delete next[key])
  return next
}

export function buildFilterLogic(form) {
  const filters = removeFriendlyFilterKeys(parseJsonField(form.filter_logic))
  const payoutLevel = Number(form.payout_level)
  const maxWinMin = Number(form.max_win_min)
  filters.guest_visible = true
  if (form.provider.trim()) filters.provider = form.provider.trim()
  const gameCodes = parseTags(form.game_codes)
  if (gameCodes.length) filters.game_codes = gameCodes
  if (form.promo_type.trim()) filters.promo_type = form.promo_type.trim()
  if (form.jackpot_type.trim()) filters.jackpot_type = form.jackpot_type.trim()
  if (form.category.trim()) filters.category = form.category.trim()
  if (form.volatility) filters.volatility = form.volatility
  if (form.payout_level && Number.isFinite(payoutLevel)) filters.payout_level = { gte: payoutLevel }
  if (form.max_win_min && Number.isFinite(maxWinMin)) filters.max_win_min = maxWinMin
  const tags = parseTags(form.theme_tags)
  if (tags.length) filters.theme_tags = tags
  const days = parseTags(form.weekday_days)
  if (days.length) filters.weekday_days = days
  if (form.time_start.trim()) filters.time_start = form.time_start.trim()
  if (form.time_end.trim()) filters.time_end = form.time_end.trim()
  return filters
}

export function buildSortLogic(form) {
  const sortLogic = parseJsonField(form.sort_logic)
  if (RULE_KIND_CONFIG[form.rule_type]?.showSortStrategy && form.sort_strategy) {
    sortLogic.strategy = form.sort_strategy
  }
  const boost = Number(form.boost_strength)
  if (BOOST_RULE_TYPES.has(form.rule_type) && Number.isFinite(boost)) {
    sortLogic.boost = boost
  }
  return sortLogic
}

export function buildConfig(form) {
  const config = parseJsonField(form.config)
  if (RULE_KIND_CONFIG[form.rule_type]?.showTone && form.tone) config.tone = form.tone
  const afterRows = Number(form.after_rows)
  if (RULE_KIND_CONFIG[form.rule_type]?.showAfterRows && Number.isFinite(afterRows)) {
    config.trigger = {
      ...(config.trigger && typeof config.trigger === 'object' ? config.trigger : {}),
      after_rows: afterRows,
    }
  }
  return config
}
